import React from 'react';
import {
  ArrowUpRight, ArrowDownRight, Minus, ShieldCheck, AlertTriangle,
  TrendingUp, Wallet, Boxes, Lightbulb, Info, HelpCircle, Package,
} from 'lucide-react';
import { formatPHP } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/* ── Helpers ──────────────────────────────────────────────────────────── */
const peso = (n) => formatPHP(Math.abs(parseFloat(n) || 0));
const signedPeso = (n) => (parseFloat(n) < 0 ? '-' : parseFloat(n) > 0 ? '+' : '') + peso(n);

const CONF = {
  high: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-slate-600 bg-slate-100 border-slate-200',
};

/* ── "?" help hint — explains the panel + reads your CURRENT value ─────── */
function HelpHint({ title, what, insight, testid, align = 'end' }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-label={`What is ${title}?`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-300 hover:text-[#1A4D2E] hover:bg-slate-100 transition-colors shrink-0"
        >
          <HelpCircle size={15} />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-80 text-left p-4" data-testid={testid ? `${testid}-content` : undefined}>
        <p className="text-sm font-bold text-slate-800 mb-1.5">{title}</p>
        <p className="text-xs text-slate-500 leading-relaxed">{what}</p>
        {insight && (
          <div className="mt-2.5 bg-[#1A4D2E]/[0.06] border border-[#1A4D2E]/15 rounded-lg px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#1A4D2E] mb-0.5">What your numbers say right now</p>
            <p className="text-xs text-slate-700 leading-relaxed">{insight}</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/* ── Circular score gauge ─────────────────────────────────────────────── */
function ScoreGauge({ value, label, sublabel, tone = 'brand', testid }) {
  const v = Math.max(0, Math.min(100, parseInt(value) || 0));
  const r = 42, c = 2 * Math.PI * r, off = c - (v / 100) * c;
  const stroke =
    tone === 'risk'
      ? v <= 20 ? '#059669' : v <= 50 ? '#d97706' : '#dc2626'
      : v >= 85 ? '#059669' : v >= 70 ? '#1A4D2E' : v >= 50 ? '#d97706' : '#dc2626';
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div className="relative shrink-0" style={{ width: 104, height: 104 }}>
        <svg width="104" height="104" className="-rotate-90">
          <circle cx="52" cy="52" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle cx="52" cy="52" r={r} fill="none" stroke={stroke} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold font-mono tracking-tight" style={{ color: stroke }}>{v}</span>
          <span className="text-[10px] text-slate-400 font-mono">/100</span>
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="text-lg font-bold" style={{ color: stroke, fontFamily: 'Manrope' }}>{sublabel}</p>
      </div>
    </div>
  );
}

/* ── KPI ribbon cell ──────────────────────────────────────────────────── */
function Kpi({ label, value, trend, trendKind, invert, help }) {
  let arrow = <Minus size={13} className="text-slate-400" />;
  let tcolor = 'text-slate-400';
  if (trend != null && trend !== 0) {
    const up = trend > 0;
    const good = invert ? !up : up;
    tcolor = good ? 'text-emerald-600' : 'text-red-600';
    arrow = up ? <ArrowUpRight size={13} className={tcolor} /> : <ArrowDownRight size={13} className={tcolor} />;
  }
  return (
    <div className="px-4 py-3 border-r border-slate-200 last:border-r-0 min-w-0">
      <div className="flex items-center justify-between gap-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 truncate">{label}</p>
        {help && <HelpHint testid={`kpi-help-${help.slug}`} title={label} what={help.what} insight={help.insight} />}
      </div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className="text-base font-mono font-semibold text-slate-800">{value}</span>
        {trend != null && (
          <span className={`flex items-center text-[11px] font-mono ${tcolor}`}>
            {arrow}{Math.abs(trend)}{trendKind || ''}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────────────── */
export default function AuditOverview({ auditData, onJumpToFindings }) {
  if (!auditData) return null;
  const k = auditData.kpis || {};
  const t = k.trend || {};
  const s = auditData.scores || {};
  const fs = auditData.findings_summary || { counts: {}, total_impact: 0 };
  const counts = fs.counts || {};
  const interps = auditData.interpretations || [];

  // Net Real Variance = money + goods position (cash discrepancy + inventory net variance)
  const cash = auditData.cash || {};
  const cashSource = cash.cash_source || (cash.ledger_supported ? 'wallet_ledger' : 'formula');
  // true_variance is set per source: Z-report over/short sum, wallet-ledger
  // variance, or physical/formula. It is the honest headline figure.
  const cashDisc = parseFloat(cash.true_variance ?? cash.discrepancy ?? 0);
  const apparent = parseFloat(cash.discrepancy ?? 0);
  const traceable = parseFloat(cash.traceable_amount ?? 0);
  const fullyTraced = !!cash.fully_traced;
  const breakdown = (cash.movement_breakdown || []).filter((m) => Math.abs(parseFloat(m.total) || 0) > 0);
  const showTrace = cashSource === 'wallet_ledger' && Math.abs(apparent) > 100 && Math.abs(traceable) > 1;
  const zr = cash.zreport_reconciliation || {};
  const showZ = cashSource === 'z_reports' && zr.supported;
  const invVar = auditData.inventory?.available
    ? parseFloat(auditData.inventory?.summary?.total_variance_capital || 0) : 0;
  const invVarRetail = auditData.inventory?.available
    ? parseFloat(auditData.inventory?.summary?.total_variance_retail || 0) : 0;
  const netReal = cashDisc + invVar;
  const netTone = Math.abs(netReal) < 100 ? 'ok' : netReal < 0 ? 'critical' : 'warning';
  const netBg = netTone === 'ok' ? 'bg-emerald-50 border-emerald-200'
    : netTone === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
  const netText = netTone === 'ok' ? 'text-emerald-700' : netTone === 'critical' ? 'text-red-700' : 'text-amber-700';

  // ── Inventory valuation (system on-hand value — always available) ───────
  const iv = auditData.inventory_valuation || {};
  const ivCap = parseFloat(iv.total_capital_value || 0);
  const ivRet = parseFloat(iv.total_retail_value || 0);
  const ivMargin = parseFloat(iv.potential_margin || 0);

  // ── Plain-English insight strings for the "?" hints (read current values) ─
  const dir = (n) => (parseFloat(n) > 0 ? 'over (excess)' : parseFloat(n) < 0 ? 'short (missing)' : 'balanced');
  const I = {
    grossMargin: `${(k.gross_margin_pct || 0).toFixed(1)}% of every peso of sales is profit after the cost of goods. ${(k.gross_margin_pct || 0) < 15 ? 'This is thin — check below-cost sales and capital costs.' : 'Healthy for agrivet retail.'}`,
    voidRate: `${(k.void_rate_pct || 0).toFixed(1)}% of transactions were voided. ${(k.void_rate_pct || 0) > 5 ? 'Elevated — frequent voids can hide cash removal; review who voids and why.' : 'Within a normal range.'}`,
    discountRate: `${(k.discount_rate_pct || 0).toFixed(1)}% of sales value was discounted. ${(k.discount_rate_pct || 0) > 5 ? 'Watch for repeat discounts to the same customers (favoritism).' : 'Modest — no concern.'}`,
    dso: `On average you wait ${(k.dso_days || 0).toFixed(0)} day(s) to collect a credit sale. Lower is better for cash flow.`,
    dpo: `On average you take ${(k.dpo_days || 0).toFixed(0)} day(s) to pay suppliers. Higher keeps cash longer, but don't miss due dates.`,
    invTurnover: `Stock turned over ${(k.inventory_turnover || 0).toFixed(1)}× this period. ${(k.inventory_turnover || 0) < 1 ? 'Slow — capital is tied up in shelves; review slow-moving stock.' : 'Goods are moving.'}`,
    netReal: auditData.inventory?.available
      ? `Your combined money + goods position is ${signedPeso(netReal)} (${dir(netReal)}). This is AFTER offsetting cash against stock — investigate only if it's beyond your tolerance.`
      : `Cash position only: ${signedPeso(cashDisc)} (${dir(cashDisc)}). Stock isn't included — run a Full Audit (with a count sheet) to see the true money + goods figure.`,
    health: `${s.health_score ?? '—'}/100 (${s.health_label || '—'}). Driven by your findings: ${counts.critical || 0} critical, ${counts.warning || 0} warning. Clear the critical items to raise it.`,
    risk: `${s.fraud_risk_score ?? '—'}/100 (${s.fraud_risk_label || '—'}). Higher = more patterns worth a closer look (voids, discounts, unexplained variances). It flags WHERE to look — it is not an accusation.`,
    z: showZ
      ? `Across ${zr.closings_count || 0} daily Z reports you are ${signedPeso(zr.over_total)} over and ${signedPeso(zr.short_total)} short — shown separately on purpose. Net is ${signedPeso(zr.overshort_total)} (${dir(zr.overshort_total)}). A steady overage usually means unrecorded sales; a short means cash actually went missing.`
      : '',
    invVal: iv.available
      ? `The system believes you hold ${peso(ivCap)} of stock at cost and ${peso(ivRet)} at selling price across ${iv.products_with_stock || 0} product(s) — a potential ${peso(ivMargin)} (${iv.margin_pct || 0}%) gross margin if it all sells. In a Full Audit you physically count against this baseline to find any shrinkage.`
      : '',
  };

  return (
    <div className="space-y-6" data-testid="audit-overview">
      {/* KPI ribbon */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-y md:divide-y-0">
          <Kpi label="Gross Margin" value={`${(k.gross_margin_pct || 0).toFixed(1)}%`} trend={t.gross_margin_pct} trendKind="pp" help={{ slug: 'gross-margin', what: 'Profit left after the cost of goods sold, as a % of sales.', insight: I.grossMargin }} />
          <Kpi label="Void Rate" value={`${(k.void_rate_pct || 0).toFixed(1)}%`} trend={t.void_rate_pct} trendKind="pp" invert help={{ slug: 'void-rate', what: 'Share of transactions that were voided/cancelled.', insight: I.voidRate }} />
          <Kpi label="Discount Rate" value={`${(k.discount_rate_pct || 0).toFixed(1)}%`} trend={t.discount_rate_pct} trendKind="pp" invert help={{ slug: 'discount-rate', what: 'Share of sales value given away as discounts.', insight: I.discountRate }} />
          <Kpi label="DSO (AR days)" value={`${(k.dso_days || 0).toFixed(0)}d`} trend={t.dso_days} trendKind="d" invert help={{ slug: 'dso', what: 'Days Sales Outstanding — average time to collect a credit sale.', insight: I.dso }} />
          <Kpi label="DPO (AP days)" value={`${(k.dpo_days || 0).toFixed(0)}d`} trend={t.dpo_days} trendKind="d" help={{ slug: 'dpo', what: 'Days Payable Outstanding — average time you take to pay suppliers.', insight: I.dpo }} />
          <Kpi label="Inv. Turnover" value={`${(k.inventory_turnover || 0).toFixed(1)}x`} trend={t.inventory_turnover} trendKind="x" help={{ slug: 'inv-turnover', what: 'How many times stock was sold and replaced in the period.', insight: I.invTurnover }} />
        </div>
      </div>

      {/* Bento: Net Real Variance hero + scores */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Net Real Variance */}
        <div className={`lg:col-span-2 rounded-xl border ${netBg} p-6 flex flex-col justify-between`} data-testid="net-variance-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Net Real Variance</p>
                <HelpHint testid="help-net-variance" title="Net Real Variance"
                  what="Your true money + goods position after offsetting cash against stock. Green = balanced, amber = net gain/overage, red = net loss."
                  insight={I.netReal} />
              </div>
              <p className="text-sm text-slate-500 mt-1 max-w-md">
                True money + goods position after offsets. {auditData.inventory?.available
                  ? 'Cash discrepancy combined with physical stock variance.'
                  : 'Cash discrepancy only (run a Full Audit to include stock).'}
              </p>
            </div>
            <div className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border ${netBg} ${netText}`}>
              {netTone === 'ok' ? 'Balanced' : netTone === 'critical' ? 'Net Loss' : 'Net Gain'}
            </div>
          </div>
          <div className="mt-4">
            <span className={`text-4xl md:text-5xl font-bold font-mono tracking-tighter ${netText}`} data-testid="net-variance-value">
              {signedPeso(netReal)}
            </span>
          </div>
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/60">
            <div className="flex items-center gap-2 text-sm">
              <Wallet size={15} className="text-slate-400" />
              <span className="text-slate-500">Cash</span>
              <span className={`font-mono font-semibold ${cashDisc < 0 ? 'text-red-600' : cashDisc > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>{signedPeso(cashDisc)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Boxes size={15} className="text-slate-400" />
              <span className="text-slate-500">Stock</span>
              <span className={`font-mono font-semibold ${auditData.inventory?.available ? (invVar < 0 ? 'text-red-600' : invVar > 0 ? 'text-emerald-600' : 'text-slate-700') : 'text-slate-300'}`}>
                {auditData.inventory?.available ? signedPeso(invVar) : 'n/a'}
              </span>
              {auditData.inventory?.available && (
                <span className="font-mono text-xs text-slate-400" data-testid="net-variance-stock-retail">
                  ({signedPeso(invVarRetail)} retail)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Scores */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col justify-center gap-6">
          <div className="relative">
            <div className="absolute top-0 right-0"><HelpHint testid="help-health-score" title="Health Score"
              what="An overall 0–100 read on how clean this period is. It goes down as critical/warning findings pile up."
              insight={I.health} /></div>
            <ScoreGauge testid="health-score-gauge" tone="brand"
              value={s.health_score} label="Health Score"
              sublabel={s.health_label || '—'} />
          </div>
          <div className="h-px bg-slate-100" />
          <div className="relative">
            <div className="absolute top-0 right-0"><HelpHint testid="help-risk-watch" title="Risk Watch"
              what="A 0–100 attention score for patterns worth a closer look (voids, discounts, unexplained variances). It points WHERE to look — not an accusation."
              insight={I.risk} /></div>
            <ScoreGauge testid="fraud-risk-gauge" tone="risk"
              value={s.fraud_risk_score} label="Risk Watch"
              sublabel={s.fraud_risk_label || '—'} />
          </div>
        </div>
      </div>

      {/* Inventory on hand — system valuation (always available) */}
      {iv.available && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="inventory-valuation-card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Package size={16} className="text-[#1A4D2E]" />
              <span className="text-sm font-bold text-slate-800">Inventory on hand — system value</span>
            </div>
            <HelpHint testid="help-inventory-valuation" title="Inventory on hand — system value"
              what="What the system believes you currently hold, valued at capital (cost) and at retail (selling price). This is the EXPECTED inventory — a Full Audit counts the shelves against it to find shrinkage."
              insight={I.invVal} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ZStat label="Value at capital" value={peso(ivCap)} sub="cost basis" />
            <ZStat label="Value at retail" value={peso(ivRet)} tone="ok" sub="selling price" />
            <ZStat label="Potential margin" value={peso(ivMargin)} tone="ok" sub={`${iv.margin_pct || 0}% if all sold`} />
            <ZStat label="Products in stock" value={iv.products_with_stock} sub={`${(iv.total_units || 0).toLocaleString()} units`} />
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Expected inventory the system holds right now — not a physical count. Run a <b>Full Audit</b> with a count sheet
            to verify the shelves against this and reveal any missing stock (valued at both cost and retail).
          </p>
        </div>
      )}

      {/* Z-report reconciliation — the cash truth for Z-Wizard branches */}
      {showZ && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="cash-zreport-card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#1A4D2E]" />
              <span className="text-sm font-bold text-slate-800">Cash reconciled from your Z reports</span>
            </div>
            <HelpHint testid="help-zreport" title="Cash reconciled from your Z reports"
              what="Your cash truth taken from each day's Z report (the over/short you already reconcile daily), not a wallet-ledger estimate. Over and short are tracked separately because they mean different things."
              insight={I.z} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ZStat label="Z reports" value={zr.closings_count} />
            <ZStat label="Total over (excess)" value={signedPeso(zr.over_total)} tone="ok" sub={`${zr.overage_days || 0} day(s)`} />
            <ZStat label="Total short (missing)" value={signedPeso(zr.short_total)} tone={Math.abs(parseFloat(zr.short_total || 0)) < 1 ? 'ok' : 'warn'} sub={`${zr.shortage_days || 0} day(s)`} />
            <ZStat label="Worst day" value={signedPeso(zr.worst_day?.over_short)} sub={zr.worst_day?.date} />
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Covering {zr.first_close || '—'} → {zr.last_close || '—'} · {zr.days_closed} day(s) closed
            {zr.days_missing > 0 ? ` · ${zr.days_missing} calendar day(s) without a Z report (rest days expected)` : ''}.
            Over and short are shown <b>separately</b> — they are different indicators. Net over/short for the period is {signedPeso(zr.overshort_total)}.
            This is the cash position you reconcile daily — not a wallet-ledger estimate.
          </p>
        </div>
      )}

      {/* Traceable cash offset — turns a phantom "short" into a paper trail */}
      {showTrace && (
        <div className={`rounded-xl border p-5 ${fullyTraced ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="cash-trace-card">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className={fullyTraced ? 'text-emerald-600' : 'text-amber-600'} />
              <span className="text-sm font-bold text-slate-800">
                {fullyTraced ? 'Traceable — not an actual short' : 'Cash movements traced'}
              </span>
              <HelpHint testid="help-cash-trace" title="Traceable cash movements"
                what="A drawer can look short simply because cash was legitimately moved (vault deposits, capital draws, expenses). This nets the apparent gap against those logged movements to reveal what is GENUINELY unexplained."
                insight={`Apparent ${signedPeso(apparent)} becomes ${signedPeso(cashDisc)} once logged movements are accounted for. ${Math.abs(cashDisc) < 100 ? 'Essentially explained — not a real shortage.' : 'A portion is still unexplained — worth a look.'}`} />
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Apparent vs ledger-true</p>
              <p className="text-sm font-mono">
                <span className="text-slate-500">{signedPeso(apparent)}</span>
                <span className="text-slate-300 mx-1.5">→</span>
                <span className={`font-bold ${Math.abs(cashDisc) < 100 ? 'text-emerald-700' : 'text-red-600'}`}>{signedPeso(cashDisc)}</span>
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            The apparent variance of <b>{signedPeso(apparent)}</b> is explained by cash movements already logged in the wallet ledger.
            Genuinely unexplained: <b className={Math.abs(cashDisc) < 100 ? 'text-emerald-700' : 'text-red-600'}>{signedPeso(cashDisc)}</b>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cash-movement-breakdown">
            {breakdown.map((m, i) => (
              <div key={i} className="flex items-center justify-between bg-white/70 border border-white rounded-lg px-3 py-2">
                <span className="text-xs text-slate-600 truncate">{m.label} <span className="text-slate-400">· {m.count}</span></span>
                <span className={`text-xs font-mono font-semibold shrink-0 ${m.direction === 'out' ? 'text-red-600' : 'text-emerald-600'}`}>{signedPeso(m.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Findings summary strip */}
      <div className="w-full bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1">
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Investigation Queue</span>
            <HelpHint testid="help-investigation-queue" title="Investigation Queue" align="start"
              what="The ranked list of things to check, sorted by severity then peso impact. Critical = act now, warning = review, watch = keep an eye on."
              insight={`${counts.critical || 0} critical, ${counts.warning || 0} warning, ${counts.watch || 0} watch finding(s); total money at stake ${peso(fs.total_impact)}. Start at the top — the critical items carry the biggest impact.`} />
          </div>
          <span className="flex items-center gap-1.5 text-sm"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="font-mono font-semibold text-red-700">{counts.critical || 0}</span> <span className="text-slate-400">critical</span></span>
          <span className="flex items-center gap-1.5 text-sm"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /><span className="font-mono font-semibold text-amber-700">{counts.warning || 0}</span> <span className="text-slate-400">warning</span></span>
          <span className="flex items-center gap-1.5 text-sm"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="font-mono font-semibold text-blue-700">{counts.watch || 0}</span> <span className="text-slate-400">watch</span></span>
        </div>
        <button onClick={onJumpToFindings} data-testid="overview-findings-strip"
          className="text-sm font-semibold text-[#1A4D2E] flex items-center gap-1 hover:gap-2 transition-all shrink-0">
          Review all <ArrowUpRight size={15} />
        </button>
      </div>

      {/* Smart Interpretations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb size={16} className="text-[#1A4D2E]" />
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Smart Interpretations</h3>
          <span className="text-xs text-slate-400">— fact-based side-notes, no AI</span>
          <HelpHint testid="help-interpretations" title="Smart Interpretations" align="start"
            what="Plain-English notes that connect the dots between sections using pure math (no AI) — e.g. 'a cash overage covers a stock shortage → likely an unrecorded sale, not a loss'. Each shows its working and a confidence."
            insight={interps.length ? `${interps.length} correlation(s) found this run. They suggest explanations to confirm — not conclusions; check the evidence before acting.` : 'No correlations yet. Run a Full Audit (cash + stock count) to unlock offset analysis like "stock shortage covered by cash overage".'} />
        </div>
        {interps.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-3 text-slate-500">
            <Info size={18} className="text-slate-400" />
            <p className="text-sm">No cross-section correlations to report. Run a <b>Full Audit</b> (with stock + cash count) to unlock offset analysis like “stock shortage covered by cash overage”.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="smart-interpretations">
            {interps.map((n, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`interpretation-card-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="font-semibold text-slate-800 text-sm leading-snug">{n.title}</p>
                  <span className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${CONF[n.confidence] || CONF.low}`}>
                    {n.confidence}
                  </span>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 mb-2">
                  <p className="font-mono text-xs text-slate-600">{n.math}</p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{n.recommendation}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ZStat({ label, value, sub, tone }) {
  const color = tone === 'ok' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-600' : 'text-slate-700';
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
      <p className={`text-sm font-bold font-mono ${color}`}>{value ?? '—'}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
